<template lang="pug">
  .bumper-set-breakdown
    .pick-plates.custom-set
      rhpa-child.pick(
        v-if="plateOpts"
        v-for="obj in plateOpts"
        :key="obj.name"
        :buyRequest="obj.buyRequest"
        :groupMeta="buyRequest"
        layout="default"
        :displayName="obj.name")
      .weight(:class="weightConfig.over && 'over-weight'")
        .weight-title
          h4 {{__('Weight:')}}
          h4 {{weightConfig.current}} / {{weightConfig.max}} {{__('LBS')}}
        .weight-bar
          .weight-bar-fill(:style="{width: weightConfig.width+'%'}")
          //- Choosing your breakdown is only available for 500LB and 1000LB products, could be updated in the future for KG
        .overweight(v-if="weightConfig.over") Remove {{weightConfig.current - weightConfig.max}} {{__('LBS to continue')}}
        .underweight(v-else)
          //- Shown from zero too, so an item being edited says what it is still waiting for
          span(v-if="weightConfig.current < weightConfig.max") {{weightConfig.max - weightConfig.current}} {{__('LBS remaining')}}
          | &nbsp;
    .set-qty(v-if="!editing")
      .set-qty-name {{__('Set Quantity')}}
      BuyRequestQty(:buyRequest="buyRequest" :hideDrawer="true")
</template>
<script setup lang="ts">
import { useProductStore } from '@/js/stores/productStore'
import rhpaChild from '@/js/components/rhpa/rhpa-child.vue'
import { createBuyRequest } from '@/js/helpers/buy-request'
import BuyRequestQty from '@/js/components/buy-request-qty.vue'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'
import { computed, onMounted, ref, watch } from 'vue'
import { useRogue } from '@/js/services/Rogue'

const props = defineProps<{
  buyRequest: BuyRequest,
  editing?: boolean,
}>()

const emit = defineEmits<{
  (e: 'plateSetValid', valid: boolean): void
}>()

const rogue = useRogue()
const productStore = useProductStore()

const products = computed(() => productStore.products)
const productId = computed(() => props.buyRequest.metadata?.groupedProductId)
const buyRequestProduct = computed(() => products.value[props.buyRequest.productId])

const plateOpts = ref<{
  buyRequest: BuyRequest;
  name: string;
  value: number;
  desc: string | undefined;
}[]>([])

watch(plateOpts, () => {
  const opts = plateOpts.value.map(opt => {
    const product = products.value[opt.buyRequest.productId]

    return {
      ...opt.buyRequest,
      name        : product.name,
      displayName : product.name.replace(/([0-9]+LB).+(Pair)(.+)?/, '$1 $2'),
      sku         : product.sku,
    }
  })
  const plateSetValid = weightConfig.value.current > 0 && weightConfig.value.current <= weightConfig.value.max
  props.buyRequest.metadata = { ...props.buyRequest.metadata, plateOpts: opts, plateSetValid }
  emit('plateSetValid', plateSetValid)
}, {
  deep: true,
})

const weightConfig = computed(() => {
  let current = 0
  plateOpts.value.forEach(opt => {
    const plateWeight = parseFloat(opt.name)
    current += plateWeight * 2 * opt.buyRequest.qty
  })

  const max = buyRequestProduct.value?.weight ?? 0

  return {
    current,
    max,
    width : max > 0 ? Math.round((current / max) * 100) : 0,
    over  : current > max,
  }
})

// The 500 and 1000 LB deep dish sets do not allow 5lb plates to be included in the selections
const deepDishSetSkus = ['USC0003-500', 'USC0003-1000']

function canChoosePlateOption(p: Product): boolean {
  if (deepDishSetSkus.includes(buyRequestProduct.value?.sku)) {
    return !['USC0005-0.25-2', 'USC0005-0.50-2', 'USC0005-5.0-2'].includes(p.sku)
  }

  return !p.name.includes('10x 10')
}

onMounted(async () => {
  if (!productId.value) {
    // Without the grouped parent there are no plates to offer, and the selector would render empty
    rogue.log.warn(`Bumper set ${buyRequestProduct.value?.sku} has no groupedProductId to load plates from`)

    return
  }

  await productStore.loadAllChildren(productId.value as number, rogue)
  const product = products.value[productId.value as number]

  if (!product) {
    rogue.log.warn(`Could not load grouped product ${productId.value} for bumper set breakdown`)

    return
  }

  product.childrenIds?.forEach(childId => {
    const prod = products.value[childId]

    if (prod) {
      const obj = {
        buyRequest : createBuyRequest(prod, rogue),
        name       : prod.name.replace(/([0-9]+LB).+(Pair)(.+)?/, '$1 $2'),
        value      : prod.productId,
        desc       : prod.attributes.short_description?.replace('(Shipping Included)', '').replace('You may note your breakdown in the comments box during checkout.', ''),
      }

      const savedQty = props.buyRequest.metadata?.plateOpts?.find(opt => opt.productId === prod.productId)?.qty
      obj.buyRequest.qty = savedQty ?? 0

      if (!prod.name.includes('Set') && canChoosePlateOption(prod)) {
        plateOpts.value.push(obj)
      }
    }
  })
})
</script>
<style lang="scss">
.bumper-rhpa .simple-child-tip {
  display: none;
}

.bumper-buy-type .swatch-holder:first-child .swatch {
  margin-left: 0;
}

.bumper-set-breakdown .pick-plates.custom-set .price,
.bumper-set-breakdown .pick-plates.custom-set .simple .product-price,
.bumper-set-breakdown .pick-plates.custom-set .rhpa-stock,
.editing .bumper-set-breakdown .pick-plates .rhpa-child.pick .simple .top .product-price.rhpa-child {
  display: none;
}

.over-weight .button {
  opacity: 0.5;
  pointer-events: none;
}

.editing .bumper-set-breakdown .pick-plates .rhpa-child.pick .simple .top {
  flex-direction: row;
  align-items: center;
}

</style>
<style lang="scss" scoped>

.bumper-set-breakdown {
  padding: 16px;
}

.buy-set {
  margin: $space-s 0;
  font-size: $size-xs;
}

.dashed {
  display: inline-block;
  border-bottom:1px dotted $color-gray-dark;
  padding-bottom:1px;
  cursor: pointer;
}

.desc {
  margin-bottom: $space-m
}

.star {
  font-size: $size-xxs;
  display: inline-block;
}

.set-qty {
  margin: $space-s 0;
}

.pick {
  margin-bottom: $space-s;
}

.set-qty-name {
  font-size: $size-xs;
  margin-bottom: $space-xs;
}

.pick-sets .pick {
  margin-bottom: $space-m;
}

.weight {
  margin-top: $space-m;
}

.weight-title {
  display: flex;
  justify-content: space-between;
}

.weight-bar {
  background-color: $color-gray-dark;
  height: $space-s;
  border-radius: $radius-default;
  margin: $space-s 0;
  position: relative;
  width: 100%;
  overflow: hidden;
}

.weight-bar-fill {
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: $color-green-dark;
  transition: width;
  transition-duration: 300ms;
  min-width: 3%;
}

.over-weight .weight-bar-fill {
  background-color: $color-red-dark;
}

.overweight {
  font-weight: bold;
  font-size: $size-xs;
  text-transform: uppercase;
  margin: $space-s 0;
}

.underweight {
  font-weight: bold;
  color: $color-gray-dark;
  font-size: $size-xs;
  text-transform: uppercase;
  margin: $space-s 0;
}

.desc {
  font-size: $size-xs
}
</style>
